import { DateTime } from "luxon";
import tools from "../../../helpers/tools";

// ============================================================
// Calendrier des EP — fonctions pures (testables sans DOM) : état d'une EP,
// regroupements par mois et par jour, filtres, libellés.
// Les données sont celles de POST eps/search (ChercherEPsResource) :
// beginAt, beginHourAt, beginMinuteAt, sector (id), jury[], startedAt,
// closedAt, endedAt, cancelledAt, isBackupSession, isFull, version,
// nbInscrits (null pour une EP V1), parametres.nbDossierMaxParEP (plafond).
// ============================================================

export const MOIS = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

export const ANNEE_DEBUT = 2021;

// Un ton par état, le même sur tous les écrans (charte, Pastille) : l'état se
// lit par le mot et le pictogramme, la couleur ne fait que l'appuyer.
export const ETATS_EP = {
  PLANIFIEE: { id: "planifiee", libelle: "Planifiée", ton: "neutral", icone: "fa-calendar-check" },
  DEMARREE: { id: "demarree", libelle: "Démarrée", ton: "ok", icone: "fa-play" },
  CLOTUREE: { id: "cloturee", libelle: "Clôturée", ton: "info", icone: "fa-hourglass-half" },
  FINALISEE: { id: "finalisee", libelle: "Finalisée", ton: "muted", icone: "fa-check-double" },
  ANNULEE: { id: "annulee", libelle: "Annulée", ton: "ko", icone: "fa-ban" },
};

// Légende de la vue compacte : « Planifiée » n'a pas de pastille.
export const LEGENDE_EP = [
  ETATS_EP.DEMARREE,
  ETATS_EP.CLOTUREE,
  ETATS_EP.FINALISEE,
  ETATS_EP.ANNULEE,
];

// Ordre de priorité identique à EPModalV2 : annulée > finalisée > clôturée >
// démarrée > planifiée.
export function etatEp(ep = {}) {
  if (ep.cancelledAt) return ETATS_EP.ANNULEE;
  if (ep.endedAt) return ETATS_EP.FINALISEE;
  if (ep.closedAt) return ETATS_EP.CLOTUREE;
  if (ep.startedAt) return ETATS_EP.DEMARREE;
  return ETATS_EP.PLANIFIEE;
}

export function heureEp(ep = {}) {
  return `${tools.numberWithLeadingZero(ep.beginHourAt || 0)}h${tools.numberWithLeadingZero(ep.beginMinuteAt || 0)}`;
}

export function dateHeureEp(ep = {}) {
  return DateTime.fromISO(ep.beginAt).set({
    hour: Number(ep.beginHourAt) || 0,
    minute: Number(ep.beginMinuteAt) || 0,
    second: 0,
    millisecond: 0,
  });
}

// `sector` est un identifiant (eps/search) ou un objet { id, name }
// (eps/mes-invitations).
export function nomSecteur(sectors = [], secteur) {
  if (secteur && typeof secteur === "object") return secteur.name || "Secteur inconnu";
  const trouve = (sectors || []).find((s) => s.id === secteur);
  return trouve ? trouve.name : "Secteur inconnu";
}

export function trierEps(eps = []) {
  return [...eps].sort((a, b) => dateHeureEp(a).toMillis() - dateHeureEp(b).toMillis());
}

export const indexMois = (ep) => DateTime.fromISO(ep.beginAt).month - 1;

export function epsDuMois(eps = [], index) {
  return trierEps(eps.filter((ep) => indexMois(ep) === index));
}

export function epsDuJour(eps = [], maintenant = DateTime.local()) {
  return trierEps(eps.filter((ep) => DateTime.fromISO(ep.beginAt).hasSame(maintenant, "day")));
}

// Prochaine EP non annulée à partir de l'instant donné (celle du jour comprise
// si elle n'a pas encore commencé).
export function prochaineEp(eps = [], maintenant = DateTime.local()) {
  return trierEps(eps.filter((ep) => !ep.cancelledAt && dateHeureEp(ep) >= maintenant))[0] || null;
}

export function juresConfirmes(ep = {}) {
  return (ep.jury || []).filter((j) => j.invitationStatus === "ACCEPT").length;
}

export function filtrerEps(eps = [], { secteur = "", masquerAnnulees = false } = {}) {
  return eps.filter((ep) => (!secteur || ep.sector === secteur) && (!masquerAnnulees || !ep.cancelledAt));
}

// Années proposées : de 2021 à l'année prochaine, dans l'ordre croissant.
export function anneesDisponibles(maintenant = DateTime.local(), depuis = ANNEE_DEBUT) {
  const fin = maintenant.year + 1;
  return Array.from({ length: Math.max(0, fin - depuis + 1) }, (_, i) => depuis + i);
}

// Une EP planifiée est « bientôt complète » quand ses inscrits atteignent 80 %
// du plafond en vigueur : le seuil suit le paramètre quand il change (CE-1399).
export const SEUIL_BIENTOT_COMPLETE = 0.8;

// Inscrits et plafond d'une EP. null quand il n'y a pas de compte à montrer :
// EP V1 (ses dossiers sont dans l'ancien modèle) ou EP annulée.
export function remplissageEp(ep = {}) {
  const plafond = ep.parametres ? ep.parametres.nbDossierMaxParEP : null;
  if (ep.cancelledAt || typeof ep.nbInscrits !== "number" || !plafond) return null;
  const inscrits = ep.nbInscrits;
  return {
    inscrits,
    plafond,
    libelle: `${inscrits} / ${plafond}`,
    bientotComplete: !ep.isFull && etatEp(ep) === ETATS_EP.PLANIFIEE && inscrits >= plafond * SEUIL_BIENTOT_COMPLETE,
  };
}

export function dossiersInscrits({ inscrits, plafond }) {
  return `${inscrits} dossier${inscrits > 1 ? "s" : ""} inscrit${inscrits > 1 ? "s" : ""} sur ${plafond}`;
}

// Vue compacte : seules les informations qui sortent de l'ordinaire ont une
// pastille. « Planifiée » est l'état par défaut et la V2 la norme : ni l'un ni
// l'autre ne s'affichent ; une EP V1 porte « V1 ».
export function pastillesEp(ep = {}) {
  const etat = etatEp(ep);
  const pastilles = [];
  if (etat !== ETATS_EP.PLANIFIEE) pastilles.push({ id: "etat", ton: etat.ton, icone: etat.icone, libelle: etat.libelle });
  if (ep.isBackupSession) pastilles.push({ id: "secours", ton: "warn", icone: "fa-history", libelle: "Secours" });
  if (ep.isFull) pastilles.push({ id: "complete", ton: "warn", icone: "fa-lock", libelle: "Complète" });
  else if (remplissageEp(ep)?.bientotComplete) {
    pastilles.push({ id: "bientot-complete", ton: "warn", icone: "fa-exclamation-triangle", libelle: "Bientôt complète" });
  }
  if (ep.version !== 2) pastilles.push({ id: "version", ton: "neutral", icone: null, libelle: "V1" });
  return pastilles;
}

// Les jurés confirmés n'intéressent que pour une EP à venir, pas encore démarrée.
export function juresAAfficher(ep = {}, maintenant = DateTime.local()) {
  if (etatEp(ep) !== ETATS_EP.PLANIFIEE) return 0;
  if (DateTime.fromISO(ep.beginAt).endOf("day") < maintenant) return 0;
  return juresConfirmes(ep);
}

export const dateLongue = (dt) => dt.setLocale("fr").toFormat("cccc d MMMM yyyy");

export function libelleAccessible(ep = {}, sectors = []) {
  const dt = DateTime.fromISO(ep.beginAt);
  const parties = [
    `EP du ${dateLongue(dt)} à ${heureEp(ep)}`,
    `secteur ${nomSecteur(sectors, ep.sector)}`,
    etatEp(ep).libelle.toLowerCase(),
  ];
  if (ep.isBackupSession) parties.push("session de secours");
  const remplissage = remplissageEp(ep);
  if (remplissage) parties.push(dossiersInscrits(remplissage));
  if (ep.isFull) parties.push("complète");
  if (remplissage?.bientotComplete) parties.push("bientôt complète");
  return parties.join(", ");
}

export function compterEps(n) {
  return n === 0 ? "Aucune EP" : `${n} EP`;
}
